import axios, {
  type AxiosRequestConfig,
  type Method,
  type AxiosResponse,
  type AxiosError
} from 'axios'

import {
  type SiteProps,
  type GPSCoordinates,
  type AutocompleteOptionProps,
  type AddressProps
} from './data'

export type SMProxyResponseProps<D> = {
  data: D | undefined
  error: string | undefined
}

export type SMProxyFindSitesDataProps = {
  sites: SiteProps[]
  location_coordinates: GPSCoordinates
}

export type SMProxyCreatePaymentIntentDataProps = {
  client_secret: string
}

export type SMProxyCreateCustomerWithReservationResponseDataProps = {
  reservation_id: string
  customer_id: string
  invoice_id: string
  payment_id: string
}

export type SMProxyCustomerWithReservationData = {
  site_id: string
  title: string | undefined
  first_name: string | undefined
  last_name: string | undefined
  address_line_1: string | undefined
  town: string | undefined
  county: string | undefined
  postcode: string | undefined
  move_in_date: Date | string | undefined
  opt_in: boolean
  email: string | undefined
  phone_number: string | undefined
  size: string | undefined
  size_code_id: string | undefined
  deposit_amount: number | undefined
  stripe_payment_ref: string | undefined
}

export type SMProxyConfirmReservationDataProps = SMProxyCustomerWithReservationData & {
  token: string | undefined
}

export type SMProxyCreateCustomerWithReservationDataProps = SMProxyCustomerWithReservationData & {
  site_photo_url: string | undefined
  unit_size: number | undefined
  duration: string | undefined
  price_per_week: number | undefined
  special_price_per_week: number | undefined
  location_search_text: string | undefined
}

export type SMProxyRequestCallbackProps = {
  site_id: string | undefined
  email: string | undefined
  phone: string | undefined
  name: string | undefined
  token: string | undefined
  reason: string | undefined
}

export type SMProxyRequestCallbackResponseProps = {
  success: boolean
  message: string
}

export type SMProxyConfirmReservationResponseProps = {
  success: boolean
  message: string
}

function formatDateToYYYYMMDD (date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const callSMProxyApiFindSites = async (
  searchValue: string
): Promise<SMProxyResponseProps<SMProxyFindSitesDataProps>> =>
  callSMProxyAPI<SMProxyFindSitesDataProps>('GET', `/sites/find/${searchValue}`)

export const callSMProxyApiCreatePaymentIntent = async (
  amount: number
): Promise<SMProxyResponseProps<SMProxyCreatePaymentIntentDataProps>> =>
  callSMProxyAPI<SMProxyCreatePaymentIntentDataProps>('POST', '/createPaymentIntent', { amount })

export const callSMProxyApiRequestCallback = async (
  payload: SMProxyRequestCallbackProps
): Promise<SMProxyResponseProps<SMProxyRequestCallbackResponseProps>> =>
  callSMProxyAPI<SMProxyRequestCallbackResponseProps>(
    'POST',
    '/requestCallback',
    payload as unknown as Record<string, unknown>
  )

export const callSMProxyApiConfirmReservation = async (
  payload: SMProxyConfirmReservationDataProps
): Promise<SMProxyResponseProps<SMProxyConfirmReservationResponseProps>> => {
  if (payload.move_in_date !== undefined && payload.move_in_date instanceof Date) {
    payload.move_in_date = formatDateToYYYYMMDD(payload.move_in_date)
  }

  return callSMProxyAPI<SMProxyConfirmReservationResponseProps>(
    'POST',
    '/confirmReservation',
    payload as unknown as Record<string, unknown>
  )
}

export const callSMProxyApiCreateCustomerWithReservation = async (
  siteId: string,
  payload: SMProxyCreateCustomerWithReservationDataProps
): Promise<SMProxyResponseProps<SMProxyCreateCustomerWithReservationResponseDataProps>> => {
  if (payload.move_in_date !== undefined && payload.move_in_date instanceof Date) {
    payload.move_in_date = formatDateToYYYYMMDD(payload.move_in_date)
  }

  return callSMProxyAPI<SMProxyCreateCustomerWithReservationResponseDataProps>(
    'POST',
    `/site/${siteId}/customerWithReservation`,
    payload as unknown as Record<string, unknown>
  )
}

export const callSMProxyAPI = async <D>(
  method: Method,
  endpoint: string,
  data: Record<string, unknown> | undefined = undefined
): Promise<SMProxyResponseProps<D>> => {
  const config: AxiosRequestConfig = {
    url: encodeURI(endpoint),
    baseURL: process.env.REACT_APP_SM_PROXY_API_HOST,
    method,
    data
  }

  try {
    const response: AxiosResponse<SMProxyResponseProps<D>> = await axios(config)
    return { data: response.data as unknown as D, error: undefined }
  } catch (error: unknown) {
    return {
      data: undefined,
      error: (((error as AxiosError).response?.data as { detail?: string })?.detail) as string | undefined
    }
  }
}

const IDEAL_BASE_URL = 'https://api.ideal-postcodes.co.uk/v1'
const IDEAL_API_KEY = process.env.REACT_APP_IDEAL_POSTCODES_API_KEY

const requireIdealKey = (): string | undefined => {
  if (!IDEAL_API_KEY) {
    console.error('Missing API key. Set REACT_APP_IDEAL_POSTCODES_API_KEY.')
    return undefined
  }
  return IDEAL_API_KEY
}

type IdealAddressAutocompleteResponse = {
  code: number
  message: string
  result?: {
    hits?: Array<{
      id: string
      suggestion: string
      udprn?: number
    }>
  }
}

type IdealResolveAddressResponse = {
  code: number
  message: string
  result?: {
    line_1?: string
    line_2?: string
    line_3?: string
    post_town?: string
    county?: string
    postcode?: string
    latitude?: number
    longitude?: number
  }
}

type IdealPlacesAutocompleteResponse = {
  code: number
  message: string
  result?: {
    hits?: Array<{
      id: string
      name: string
      descriptive_name?: string
      country_iso?: string
      language?: string
    }>
  }
}

type IdealResolvePlaceResponse = {
  code: number
  message: string
  result?: {
    place: string
    name: string
    descriptive_name?: string
    latitude: number
    longitude: number
    country_iso?: string
    language?: string
  }
}

const IDEAL_LIMIT = 10

export const fetchAddressAutocompleteSuggestions = async (
  searchValue: string
): Promise<AutocompleteOptionProps[]> => {
  const key = requireIdealKey()
  if (!key) return []

  const q = (searchValue ?? '').trim()
  if (q.length < 1) return [] 

  try {
    const { data } = await axios.get<IdealAddressAutocompleteResponse>(
      `${IDEAL_BASE_URL}/autocomplete/addresses`,
      { params: { query: q, api_key: key, limit: IDEAL_LIMIT } }
    )

    const hits = data?.result?.hits ?? []
    return hits.map(hit => ({ id: hit.id, label: hit.suggestion }))
  } catch (e) {
    console.error('Failed to get address suggestions (Ideal Postcodes)', e)
    return []
  }
}

const isUkCountryIso = (iso?: string) => {
  const v = (iso ?? '').trim().toUpperCase()
  return v === '' || v === 'GB' || v === 'GBR' || v === 'UK'
}

export const fetchLocationAutocompleteSuggestions = async (
  searchValue: string
): Promise<AutocompleteOptionProps[]> => {
  const key = requireIdealKey()
  if (!key) return []

  const q = (searchValue ?? '').trim()
  if (q.length < 1) return [] 

  try {
    const [placesRes, addressesRes] = await Promise.all([
      axios.get<IdealPlacesAutocompleteResponse>(
        `${IDEAL_BASE_URL}/places`,
        { params: { query: q, api_key: key, limit: IDEAL_LIMIT } }
      ),
      axios.get<IdealAddressAutocompleteResponse>(
        `${IDEAL_BASE_URL}/autocomplete/addresses`,
        { params: { query: q, api_key: key, limit: IDEAL_LIMIT } }
      )
    ])

    const placeHits = placesRes.data?.result?.hits ?? []
    const addressHits = addressesRes.data?.result?.hits ?? []

    const placeOptions: AutocompleteOptionProps[] = placeHits
      .filter(h => isUkCountryIso(h.country_iso))
      .map(h => ({
        id: h.id,
        label: (h.descriptive_name || h.name || '').trim()
      }))
      .filter(o => o.label.length > 0)

    const addressOptions: AutocompleteOptionProps[] = addressHits
      .map(h => ({
        id: h.id,
        label: (h.suggestion || '').trim()
      }))
      .filter(o => o.label.length > 0)

    const seen = new Set<string>()
    const merged: AutocompleteOptionProps[] = []

    for (const opt of [...placeOptions, ...addressOptions]) {
      const k = opt.label.trim().toLowerCase()
      if (!k || seen.has(k)) continue
      seen.add(k)
      merged.push(opt)
    }

    return merged
  } catch (e) {
    console.error('Failed to get location suggestions (Ideal Postcodes)', e)
    return []
  }
}

export const fetchAddressDetails = async (addressId: string): Promise<AddressProps> => {
  const empty: AddressProps = {
    id: undefined,
    address: undefined,
    town: undefined,
    county: undefined,
    postcode: undefined,
    fullAddress: undefined
  }

  const key = requireIdealKey()
  if (!key) return empty

  const id = (addressId ?? '').trim()
  if (!id) return empty

  try {
    const { data } = await axios.get<IdealResolveAddressResponse>(
      `${IDEAL_BASE_URL}/autocomplete/addresses/${encodeURIComponent(id)}/gbr`,
      { params: { api_key: key } }
    )

    const r = data?.result
    if (!r) return empty

    const l1 = (r.line_1 ?? '').trim()
    const l2 = (r.line_2 ?? '').trim()
    const l3 = (r.line_3 ?? '').trim()
    const town = (r.post_town ?? '').trim()
    const county = (r.county ?? '').trim()
    const postcode = (r.postcode ?? '').trim()

    const addressLine = [l1, l2, l3].filter(Boolean).join(' ').trim()
    const fullAddress = [l1, l2, l3, town, county, postcode].filter(Boolean).join(', ')

    return {
      id,
      address: addressLine || undefined,
      town: town || undefined,
      county: county || undefined,
      postcode: postcode || undefined,
      fullAddress: fullAddress || undefined
    }
  } catch (e) {
    console.error('Failed to get address details (Ideal Postcodes)', e)
    return empty
  }
}

export const fetchPlaceDetails = async (
  placeId: string
): Promise<GPSCoordinates | undefined> => {
  const key = requireIdealKey()
  if (!key) return undefined

  const id = (placeId ?? '').trim()
  if (!id) return undefined

  try {
    const { data } = await axios.get<IdealResolvePlaceResponse>(
      `${IDEAL_BASE_URL}/places/${encodeURIComponent(id)}`,
      { params: { api_key: key } }
    )

    const r = data?.result
    if (!r) return undefined

    return [r.latitude, r.longitude]
  } catch (e) {
    console.error('Failed to resolve place (Ideal Postcodes)', e)
    return undefined
  }
}
